import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, Typography, Button, Input, Row, Col, Space, Table, Grid, message, Tag, List } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { getPayrolls, deletePayroll } from '@/api/payroll';
import type { Payroll } from '@/types/employee/payroll';
import PayrollModal from '@/components/features/employee/PayrollModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import { useAuth } from '@/contexts/AuthContext';
import type { QueryDocumentSnapshot } from 'firebase/firestore';
import dayjs from 'dayjs';

const { Title } = Typography;
const PAGE_SIZE = 10;

// Type for delete error that matches DeleteConfirmationModal interface
type DeleteError = Error & { 
  dependencies?: Array<{ 
    type: string; 
    items: Array<{ id: string; name?: string; number?: string; title?: string }> 
  }>; 
  blockName?: string; 
  unitNumber?: string; 
  amenityName?: string 
};

export const PayrollPage: React.FC = () => {
  const { t } = useTranslation(['payroll', 'common']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const screens = Grid.useBreakpoint();
  const { firestoreUser } = useAuth();
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPayroll, setEditingPayroll] = useState<Payroll | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<DeleteError | undefined>(undefined);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: PAGE_SIZE,
    total: 0
  });
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot | undefined>();

  // Queries
  const { data: payrollsData = { payrolls: [], lastDoc: undefined, total: 0 }, isLoading } = useQuery<{
    payrolls: Payroll[];
    lastDoc?: QueryDocumentSnapshot;
    total: number;
  }>({
    queryKey: ['payrolls', pagination.current, pagination.pageSize, searchTerm, firestoreUser?.workspace?.id],
    queryFn: async () => {
      if (!firestoreUser?.workspace?.id) {
        throw new Error('No workspace ID available');
      }
      const currentLastDoc = pagination.current > 1 ? lastDoc : undefined;
      return getPayrolls({
        searchTerm,
        pageSize: pagination.pageSize,
        page: pagination.current,
        lastDoc: currentLastDoc,
        firestoreUser
      });
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    placeholderData: (previousData) => previousData,
    enabled: !!firestoreUser?.workspace?.id
  });

  // Update lastDoc when new data arrives
  React.useEffect(() => {
    if (payrollsData.lastDoc) {
      setLastDoc(payrollsData.lastDoc);
    }
  }, [payrollsData.lastDoc]);

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deletePayroll(id, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('payroll:deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['payrolls'] });
      setOpenDeleteDialog(false);
      setDeleteId(null);
      setDeleteError(undefined);
    },
    onError: (error: Error) => {
      setDeleteError(error as DeleteError);
      // Don't close modal on error - let DeleteConfirmationModal handle it
    }
  });

  // Handlers
  const handleAdd = useCallback(() => {
    setEditingPayroll(null);
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback((payroll: Payroll) => {
    setEditingPayroll(payroll);
    setIsModalOpen(true);
  }, []);

  const handleDelete = useCallback((payroll: Payroll) => {
    setDeleteId(payroll.id);
    setDeleteError(undefined);
    setOpenDeleteDialog(true);
  }, []);

  const handleViewDetails = useCallback((payroll: Payroll) => {
    navigate(`/employee/payroll/${payroll.id}`, { 
      state: { 
        payroll,
        from: 'list'
      } 
    });
  }, [navigate]);

  const handleModalSuccess = useCallback(() => {
    setIsModalOpen(false);
    setEditingPayroll(null);
    queryClient.invalidateQueries({ queryKey: ['payrolls'] });
  }, [queryClient]);

  const handleModalCancel = useCallback(() => {
    setIsModalOpen(false);
    setEditingPayroll(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
    }
  }, [deleteId, deleteMutation]);

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setDeleteId(null);
    setDeleteError(undefined);
  };

  const handleErrorDismiss = () => {
    setDeleteError(undefined);
  };

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  }, []);

  const handleTableChange = useCallback((pagination: any) => {
    setPagination(prev => ({ 
      ...prev, 
      current: pagination.current,
      pageSize: pagination.pageSize || prev.pageSize
    }));
  }, []);

  // Use the data directly from the API (no client-side filtering)
  const payrolls = payrollsData.payrolls || [];

  const getStatusTagColor = (status: string) => {
    if (!status) return 'default';
    switch (status) {
      case 'pending': return 'orange';
      case 'processed': return 'green';
      case 'rejected': return 'red';
      default: return 'default';
    }
  };

  const formatMoney = (amount: any) => {
    if (!amount || typeof amount !== 'object') return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: amount.currency || 'USD'
    }).format(amount.amount);
  };

  const renderPayrollCard = (payroll: Payroll) => (
    <Card
      key={payroll.id}
      hoverable
      style={{ marginBottom: 16 }}
      actions={[
        <Button
          key="view"
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(payroll)}
        >
          {t('common:actions.view')}
        </Button>,
        <Button
          key="edit"
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEdit(payroll)}
        >
          {t('common:actions.edit')}
        </Button>,
        <Button
          key="delete"
          type="link"
          icon={<DeleteOutlined />}
          danger
          onClick={() => handleDelete(payroll)}
        >
          {t('common:actions.delete')}
        </Button>,
      ]}
    >
      <Card.Meta
        title={payroll.employee.name}
        description={
          <Space direction="vertical" size="small">
            <div>
              <strong>{t('payroll:period')}:</strong> {dayjs(payroll.period.startDate).format('MMM DD, YYYY')} - {dayjs(payroll.period.endDate).format('MMM DD, YYYY')}
            </div>
            <div>
              <strong>{t('payroll:grossSalary')}:</strong> {formatMoney(payroll.grossSalary)}
            </div>
            <div>
              <strong>{t('payroll:netSalary')}:</strong> {formatMoney(payroll.netSalary)}
            </div>
            <div>
              <Tag color={getStatusTagColor(payroll.status)}>
                {payroll.status && ['pending', 'processed', 'rejected'].includes(payroll.status) 
                  ? t(`payroll:status.${payroll.status}`) 
                  : (payroll.status || '-')}
              </Tag>
            </div>
          </Space>
        }
      />
    </Card>
  );

  // Show error message if query fails
  React.useEffect(() => {
    if (!payrollsData.payrolls) {
      message.error(t('payroll:fetchError'));
    }
  }, [payrollsData.payrolls, t]);

  const columns = [
    {
      title: t('payroll:employee'),
      dataIndex: ['employee', 'name'],
      key: 'employee',
    },
    {
      title: t('payroll:period'),
      key: 'period',
      render: (_: any, record: Payroll) => (
        <span>
          {dayjs(record.period.startDate).format('MMM DD, YYYY')} - {dayjs(record.period.endDate).format('MMM DD, YYYY')}
        </span>
      ),
    },
    {
      title: t('payroll:grossSalary'),
      dataIndex: 'grossSalary',
      key: 'grossSalary',
      render: (amount: any) => formatMoney(amount),
    },
    {
      title: t('payroll:netSalary'),
      dataIndex: 'netSalary',
      key: 'netSalary',
      render: (amount: any) => formatMoney(amount),
    },
    {
      title: t('payroll:statusLabel'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const validStatuses = ['pending', 'processed', 'rejected'];
        const isValidStatus = status && validStatuses.includes(status);
        return (
          <Tag color={getStatusTagColor(status)}>
            {isValidStatus ? t(`payroll:status.${status}`) : (status || '-')}
          </Tag>
        );
      },
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      render: (_: any, record: Payroll) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            {t('common:actions.view')}
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {t('common:actions.edit')}
          </Button>
          <Button
            type="link"
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record)}
          >
            {t('common:actions.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: '100%', padding: screens.md ? 24 : 16 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>{t('common:navigation.payroll')}</Title>
        </Col>
        <Col>
          <Space direction={screens.xs ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
            <Input.Search
              placeholder={t('common:searchPlaceholder', { entity: 'payroll' })}
              allowClear
              onSearch={handleSearch}
              style={{ width: screens.xs ? '100%' : 200 }}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              {t('payroll:actions.add')}
            </Button>
          </Space>
        </Col>
      </Row>

      <Card>
        {screens.xs ? (
          <List
            dataSource={payrolls}
            renderItem={renderPayrollCard}
            rowKey="id"
            loading={isLoading}
            grid={{ gutter: 16, xs: 1, sm: 1 }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={payrolls}
            rowKey="id"
            loading={isLoading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: payrollsData.total || 0,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                t('common:showingRange', { 
                  from: range[0], 
                  to: range[1], 
                  total 
                })
            }}
            onChange={handleTableChange}
            scroll={{ x: 'max-content' }}
          />
        )}
      </Card>

      <PayrollModal
        open={isModalOpen}
        payroll={editingPayroll}
        onSuccess={handleModalSuccess}
        onCancel={handleModalCancel}
      />

      <DeleteConfirmationModal
        visible={openDeleteDialog}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t('common:confirmDelete')}
        error={deleteError}
        onErrorDismiss={handleErrorDismiss}
      />
    </div>
  );
};

export default PayrollPage; 